import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, TrendingDown, Database, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HowItWorks = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dldStatus, setDldStatus] = useState(null);

  useEffect(() => {
    // Fetch DLD data status
    const fetchDldStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/dld-status`);
        setDldStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch DLD status:', error);
      }
    };
    fetchDldStatus();
  }, []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header - Click to toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
            <TrendingDown className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">How We Calculate "Below Market Average"</h3>
            <div className="flex items-center gap-2 mt-1">
              {dldStatus?.using_real_dld_data ? (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <Database size={12} />
                  Using real DLD data • {dldStatus.transaction_count.toLocaleString()} transactions
                </span>
              ) : (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle size={12} />
                  Using 2026 market benchmarks
                </span>
              )}
            </div>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content - Shows when open */}
      {isOpen && (
        <div className="px-6 pb-6 space-y-6 border-t border-gray-100">
          {/* Step 1 */}
          <div className="flex gap-4 pt-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Database className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">1. We Track Real Sales</h4>
              {dldStatus?.using_real_dld_data ? (
                <p className="text-sm text-gray-600">
                  We collect actual property sale prices from the Dubai Land Department's official database. 
                  Currently using <span className="font-semibold">{dldStatus.transaction_count.toLocaleString()} real transactions</span>.
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  We use verified 2026 market benchmarks with regional price variations for 15 Dubai areas until 
                  official DLD transaction data is imported.
                </p>
              )}
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-semibold text-sm">3M</span>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">2. Calculate 3-Month Average</h4>
              {dldStatus?.using_real_dld_data ? (
                <p className="text-sm text-gray-600">
                  We take all sales from the last 3 months for properties with similar bedrooms and location, 
                  then calculate the average price from actual market transactions.
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  We apply area-specific multipliers (e.g., Marina ×1.15, Downtown ×1.25) to baseline 
                  prices adjusted by property size.
                </p>
              )}
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">3. Find the Deals</h4>
              <p className="text-sm text-gray-600">
                Properties priced 10% or more below this average are highlighted as deals. Simple as that!
              </p>
            </div>
          </div>

          {/* Example */}
          <div className="bg-gray-50 rounded-lg p-4 mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Example:</p>
            <div className="text-sm text-gray-600 space-y-1">
              <p>• 2BR apartments in Dubai Marina average: <span className="font-semibold">AED 1.8M</span></p>
              <p>• We found one listed at <span className="font-semibold text-emerald-600">AED 1.5M</span></p>
              <p>• That's <span className="font-semibold text-emerald-600">16% below average</span> = Deal! 🎉</p>
            </div>
          </div>

          {/* Data Source */}
          <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
            {dldStatus ? (
              <>Data Source: {dldStatus.data_source} • Last Updated: {new Date(dldStatus.last_updated).toLocaleDateString()}</>
            ) : (
              <>Data Source: Market Analysis • Updated Monthly</>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HowItWorks;
